import type { EmailParticipant } from "@onemileauto/shared-types";

/** Short, glanceable relative time for a thread-list row ("2h", "Yesterday", "Aug 12"). */
export function formatRelativeTime(iso: string, now: Date = new Date()): string {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return "";

  const diffMs = now.getTime() - then.getTime();
  const diffMin = Math.round(diffMs / 60_000);

  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin}m`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24 && isSameCalendarDay(then, now)) return `${diffHr}h`;

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (isSameCalendarDay(then, yesterday)) return "Yesterday";

  const diffDays = Math.round((startOfDay(now).getTime() - startOfDay(then).getTime()) / 86_400_000);
  if (diffDays < 7) return then.toLocaleDateString(undefined, { weekday: "short" });

  const sameYear = then.getFullYear() === now.getFullYear();
  return then.toLocaleDateString(undefined, sameYear ? { month: "short", day: "numeric" } : { month: "short", day: "numeric", year: "numeric" });
}

/** Longer form for the thread-detail message header ("Aug 17, 2026, 10:00 AM"). */
export function formatFullTimestamp(iso: string): string {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return "";
  return then.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function isSameCalendarDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function labelFor(p: EmailParticipant): string {
  return p.name?.trim() || p.email;
}

/**
 * Who a thread is "with", from the caller's point of view — every
 * participant except the mailbox's own address, deduped, joined for
 * display. Falls back to "you" for a thread with only the mailbox owner
 * (e.g. a note the mailbox sent to itself) rather than showing nothing.
 */
export function displayParticipants(participants: EmailParticipant[], selfEmail?: string): string {
  const self = selfEmail?.toLowerCase();
  const others = participants.filter((p) => p.email.toLowerCase() !== self);
  const list = others.length > 0 ? others : participants;
  if (list.length === 0) return "(no participants)";
  if (list.length <= 2) return list.map(labelFor).join(", ");
  return `${list
    .slice(0, 2)
    .map(labelFor)
    .join(", ")} +${list.length - 2}`;
}

/** Single letter/initial for the small avatar circle in a thread-list row. */
export function initialFor(participants: EmailParticipant[], selfEmail?: string): string {
  const self = selfEmail?.toLowerCase();
  const other = participants.find((p) => p.email.toLowerCase() !== self) ?? participants[0];
  const source = other?.name?.trim() || other?.email || "?";
  return source.charAt(0).toUpperCase();
}
